import type { ComponentType, ReactNode } from 'react'

import { cn } from '@/lib/cn'

import { Button } from './Button'

export interface EmptyStateAction {
  label: string
  onClick: () => void
  icon?: ComponentType<{ className?: string }>
}

export interface EmptyStateProps {
  /** Pass the icon component itself, e.g. `icon={Music}` — not an element. */
  icon: ComponentType<{ className?: string }>
  title: string
  description?: string
  action?: EmptyStateAction
  /** A second, lesser route out of the empty state. Rendered beside `action`. */
  secondaryAction?: EmptyStateAction
  /** Escape hatch for a custom action row. Rendered under `action`. */
  children?: ReactNode
  className?: string
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  children,
  className,
}: EmptyStateProps) {
  const ActionIcon = action?.icon
  const SecondaryIcon = secondaryAction?.icon

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-16 text-center',
        className,
      )}
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-hover">
        <Icon className="h-6 w-6 text-text-subtle" aria-hidden="true" />
      </span>

      <div className="max-w-sm space-y-1">
        <p className="text-base font-medium text-text">{title}</p>
        {description ? <p className="text-sm text-text-muted">{description}</p> : null}
      </div>

      {action || secondaryAction ? (
        // Wraps rather than shrinks: two buttons squeezed onto one line on a
        // phone truncate their labels, and a label is all these buttons are.
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
          {action ? (
            <Button onClick={action.onClick}>
              {ActionIcon ? <ActionIcon className="h-4 w-4" aria-hidden="true" /> : null}
              {action.label}
            </Button>
          ) : null}
          {secondaryAction ? (
            <Button variant="secondary" onClick={secondaryAction.onClick}>
              {SecondaryIcon ? <SecondaryIcon className="h-4 w-4" aria-hidden="true" /> : null}
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>
      ) : null}

      {children}
    </div>
  )
}
